/**
 * Anthropic Claude model pricing table and cost calculation utilities.
 *
 * Prices are per million tokens (MTok). The frontend applies these to
 * raw token counts stored on DocumentQueue.tokenUsage[] to compute
 * per-document processing cost.
 *
 * Pricing source: https://docs.anthropic.com/en/docs/about-claude/pricing
 * Last updated: 2026-03-23
 */

const MODEL_PRICING = {
  'claude-opus-4': { input: 5, output: 25, cacheWrite: 6.25, cacheRead: 0.50 },
  'claude-sonnet-4': { input: 3, output: 15, cacheWrite: 3.75, cacheRead: 0.30 },
  'claude-haiku-4': { input: 1, output: 5, cacheWrite: 1.25, cacheRead: 0.10 },
  'claude-haiku-3': { input: 0.25, output: 1.25, cacheWrite: 0.30, cacheRead: 0.03 },
};

const DEFAULT_PRICING = MODEL_PRICING['claude-sonnet-4'];

/**
 * Resolve pricing tier for a model ID by progressively shortening it.
 * "claude-sonnet-4-6" → "claude-sonnet-4" → hit
 */
export function resolveModelPricing(modelId) {
  if (!modelId) return DEFAULT_PRICING;
  const parts = modelId.split('-');
  for (let len = parts.length; len >= 2; len--) {
    const prefix = parts.slice(0, len).join('-');
    if (MODEL_PRICING[prefix]) return MODEL_PRICING[prefix];
  }
  return DEFAULT_PRICING;
}

function calculateEntryCost(entry) {
  const pricing = resolveModelPricing(entry.model);
  const inputCost = (entry.inputTokens || 0) * pricing.input / 1_000_000;
  const outputCost = (entry.outputTokens || 0) * pricing.output / 1_000_000;
  const cacheWriteCost = (entry.cacheCreationTokens || 0) * pricing.cacheWrite / 1_000_000;
  const cacheReadCost = (entry.cacheReadTokens || 0) * pricing.cacheRead / 1_000_000;
  return inputCost + outputCost + cacheWriteCost + cacheReadCost;
}

const STEP_LABELS = {
  classify: 'Classify',
  extract: 'Extract',
  agent_orchestration: 'Agent',
  evaluate: 'Evaluate',
  partner_analysis: 'Partner Analysis',
};

/**
 * Calculate total document processing cost from token usage array.
 * @param {Array} tokenUsage - Array of token usage entries from DocumentQueue
 * @returns {{ totalCost: number, breakdown: Array<{ step: string, label: string, model: string, cost: number }> }}
 */
export function calculateDocumentCost(tokenUsage) {
  if (!tokenUsage || tokenUsage.length === 0) {
    return { totalCost: 0, breakdown: [] };
  }
  const breakdown = tokenUsage.map(entry => ({
    step: entry.step,
    label: STEP_LABELS[entry.step] || entry.step,
    model: entry.model,
    cost: calculateEntryCost(entry),
  }));
  const totalCost = breakdown.reduce((sum, b) => sum + b.cost, 0);
  return { totalCost, breakdown };
}

/**
 * Format a cost value as a dollar string with 4 decimal places.
 */
export function formatCost(cost) {
  return `$${cost.toFixed(4)}`;
}
